import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchOrganizationProfile,
  updateOrganizationProfile,
} from '@/lib/api/organization-profile'
import type { OrganizationProfileFormData } from '@/types/organization-profile'
import { toast } from 'sonner'

/**
 * Query key factory for organization profile
 * Provides consistent keys for cache management
 */
export const organizationProfileKeys = {
  all: ['organization-profile'] as const,
  details: () => [...organizationProfileKeys.all, 'detail'] as const,
  detail: (organizationId: string) =>
    [...organizationProfileKeys.details(), organizationId] as const,
}

/**
 * Fetch organization profile for current organization
 */
export function useOrganizationProfile() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: organizationProfileKeys.detail(organizationId || ''),
    queryFn: () => fetchOrganizationProfile(organizationId!),
    enabled: !!organizationId,
  })
}

/**
 * Update organization profile
 */
export function useUpdateOrganizationProfile() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrganizationProfileFormData> }) =>
      updateOrganizationProfile(id, data),
    onSuccess: () => {
      // Invalidate the organization profile query
      const organizationId = currentOrganization?.id
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: organizationProfileKeys.detail(organizationId),
        })
      }
      toast.success('Organization profile updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update organization profile', {
        description: error.message,
      })
    },
  })
}
